import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu, X, LogOut, ShieldCheck } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const linkBase =
    "tracking-eyebrow text-[0.72rem] hover:text-[var(--eafg-copper)] transition-colors";
  const active = "text-[var(--eafg-copper)]";

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 border-b border-[var(--eafg-line)] bg-[#F8F7F5]/85 backdrop-blur-xl"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex flex-col leading-none" data-testid="navbar-logo-link">
          <span className="font-serif-display text-2xl md:text-[26px] tracking-tight text-[var(--eafg-ink)]">
            Esmalte <span className="italic text-[var(--eafg-copper)]">al fuego</span>
          </span>
          <span className="tracking-eyebrow text-[var(--eafg-muted)] mt-1 hidden md:block">
            Guerrero · León, España
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink
            to="/"
            end
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
            data-testid="nav-link-home"
          >
            {t("nav.home")}
          </NavLink>
          <NavLink
            to="/tienda"
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
            data-testid="nav-link-shop"
          >
            {t("nav.shop")}
          </NavLink>
          <NavLink
            to="/contacto"
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
            data-testid="nav-link-contact"
          >
            {t("nav.contact")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="tracking-eyebrow text-[0.7rem] hover:text-[var(--eafg-copper)]"
            data-testid="lang-toggle-button"
          >
            {lang === "es" ? "ES · EN" : "EN · ES"}
          </button>

          {user && user.role === "admin" && (
            <Link
              to="/admin"
              className="hidden md:inline-flex items-center gap-1 tracking-eyebrow text-[0.7rem] hover:text-[var(--eafg-copper)]"
              data-testid="nav-link-admin"
            >
              <ShieldCheck size={14} strokeWidth={1.5} /> {t("nav.admin")}
            </Link>
          )}

          {user ? (
            <Link
              to="/cuenta"
              className="hidden md:inline-flex items-center gap-1 hover:text-[var(--eafg-copper)]"
              data-testid="nav-link-account"
            >
              <User size={18} strokeWidth={1.5} />
            </Link>
          ) : (
            <Link
              to="/login"
              className="hidden md:inline-flex items-center gap-1 hover:text-[var(--eafg-copper)]"
              data-testid="nav-link-login"
            >
              <User size={18} strokeWidth={1.5} />
            </Link>
          )}

          {user && (
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="hidden md:inline-flex items-center gap-1 hover:text-[var(--eafg-copper)]"
              data-testid="nav-logout-button"
              title={t("nav.logout")}
            >
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          )}

          <Link
            to="/carrito"
            className="relative hover:text-[var(--eafg-copper)]"
            data-testid="nav-cart-link"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[var(--eafg-copper)] text-white text-[10px] font-medium w-5 h-5 inline-flex items-center justify-center"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            className="md:hidden"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--eafg-line)] bg-[#F8F7F5]">
          <div className="px-6 py-6 flex flex-col gap-5">
            <NavLink to="/" onClick={() => setMobileOpen(false)} className={linkBase}>
              {t("nav.home")}
            </NavLink>
            <NavLink to="/tienda" onClick={() => setMobileOpen(false)} className={linkBase}>
              {t("nav.shop")}
            </NavLink>
            <NavLink to="/contacto" onClick={() => setMobileOpen(false)} className={linkBase}>
              {t("nav.contact")}
            </NavLink>
            {user ? (
              <NavLink to="/cuenta" onClick={() => setMobileOpen(false)} className={linkBase}>
                {t("nav.account")}
              </NavLink>
            ) : (
              <NavLink to="/login" onClick={() => setMobileOpen(false)} className={linkBase}>
                {t("nav.login")}
              </NavLink>
            )}
            {user && user.role === "admin" && (
              <NavLink to="/admin" onClick={() => setMobileOpen(false)} className={linkBase}>
                {t("nav.admin")}
              </NavLink>
            )}
            {user && (
              <button
                onClick={async () => {
                  await logout();
                  setMobileOpen(false);
                  navigate("/");
                }}
                className={`${linkBase} text-left`}
              >
                {t("nav.logout")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
